/*
    collect, store and return various metrics
    mostly used to collect and display statistics for debug and service purpose
 */
export class statCollector {

    /*
    properties: struct where keys - parameter name, val - scalar, function or struct
    displayContainer - DOM container where stat will be written
    formatters - struct where keys - field name, val - predefined formatter name ('round2', 'round6', 'timestamp', 'int')
     */
    constructor(id, properties, displayContainer, formatters) {
        this.id = id;
        this.info = properties;
        this.displayContainer = displayContainer;
        this.formatters = formatters;
        this.maxStringLength = 40;
        this.shrinkLongLines = true;
        this.formattersHandlers = {
            round6: function (v) {
                return v.toFixed(6);
            },
            round2: function (v) {
                return v.toFixed(2);
            },
            int: function (v) {
                return v.toFixed(0);
            },
            timestamp: function (v) {
                if (v == 0)
                    return "-";
                return (((new Date().getTime() - v)/1000).toFixed(1)) + 's';
            },
            json: function (v) {
                return stringifyValue(v);
            },
            LatLngBounds: function (v) {
                try {
                    return { "UpLeft, DownRight" : this.locationHuman(v.getNorthWest()) + " : "+ this.locationHuman(v.getSouthEast()) }
                } catch (e) {
                    return "[wrong data]"
                }
            }.bind(this)
        }
    }

    getId() {
        return this.id
    }

    set(key, val) {
        if (typeof this.info[key] !== "function")
            this.info[key] = val;
    }

    /**
     * Replaces all mutable statistic values with a new key-value object.
     *
     * @param {object} properties New statistics object. Non-object values reset the collector to an empty object.
     * @returns {void}
     */
    replace(properties) {
        this.info = properties && typeof properties === "object" ? properties : {};
    }

    add(key, val) {
        if (typeof this.info[key] !== "function")
            this.info[key] += !val?1:val;
    }
    dec(key, val) {
        if (typeof this.info[key] !== "function")
            this.info[key] -= !val?1:val;
    }
    get(key) {
        if (typeof this.info[key] !== "function") {
            return this.info[key];
        } else {
            return this.info[key](key);
        }
    }

    getHuman(key, val) {
        let idx;
        if (val == null) {
            console.warn("Unknown stat property: " + key);
            return {name: key, value: ""};
        }

        //do some formatting
        if ((idx = key.indexOf("Timestamp")) > -1) {
            key = key.slice(0, idx);
            val = this.formattersHandlers.timestamp(val);
        }

        let formatter = this._hasFormatter(key)
        if (formatter != null) {
            val = formatter(val);
            return {name: key, shortValue: val, longValue: val};
        }

        // basic formating
        if (typeof val === "object") {
            val = stringifyValue(val);
        } else {
            val = val.toString();
        }

        if (this.shrinkLongLines)
            val = this._shrinkString(val, this.maxStringLength, true)

        return {name: key, shortValue: val, longValue: val}
    }

    dump() {
        let stat = {};
        for (let el of Object.entries(this.info)) {
            let data = this.getHuman(el[0], this.get(el[0]));
            stat[data.name] = data.shortValue
        }
        return stat
    }

    setDisplayContainer(container) {
        this.displayContainer = container;
    }

    show() {
        if (! this.displayContainer) {
            console.warn("statCollector: display container isn't defined");
            return;
        }

        //if block is hidden
        if (this.displayContainer.offsetParent == null)
            return;

        let html = '';

        for (let el of Object.entries(this.info)) {
            let name = el[0];
            let val = this.get(el[0]);
            let title = "";
            if (typeof val === 'object') {
                let formatter = this._hasFormatter(name)
                if (formatter != null) {
                    val = formatter(val);
                    val = this._jsonToHtml(val);
                } else {
                    val = this._jsonToHtml(val);
                }
            } else {
                let data = this.getHuman(name, val);
                name = data.name;
                title = data.longValue;
                val = data.shortValue;
            }
            html += `<li title="${title}" style="display: flex;"><div>${name}:&nbsp;</div><div>${val}</div></li>`;
        }
        this.displayContainer.innerHTML = "<ul>" + html + "</ul>";
    }

    /**
     * Formats a value as escaped pretty JSON inside an inline preformatted HTML element.
     *
     * @param {*} value Value to render as JSON or text.
     * @returns {string} HTML string that preserves JSON indentation and line breaks.
     */
    _jsonToHtml(value) {
        const text = typeof value === "string" ? value : stringifyValue(value);

        return `<pre style="white-space: pre-wrap; margin: 0; overflow-wrap: anywhere;">${escapeHtml(text)}</pre>`;
    }

    _objectToHtml(obj) {
        if (!obj)
            return "[no data]";
        let first = true;
        let html = "";
        for (let el of Object.entries(obj)) {
            if (first) {
                first = false;
            } else {
                html += ", ";
            }
            let data = this.getHuman(el[0], el[1]);
            html += `<span title="${data.name}">${data.shortValue}</span>`;
        }
        return html;
    }

    //shrink string to maxLength
    _shrinkString(val, maxLength, centerCut) {
        let len = maxLength ? maxLength : 25;

        if (val.length > len) {
            let pLen = Math.floor((len - 3) / 2);
            if (centerCut) {
                val = val.substr(0, pLen) + "..." + val.substr(val.length - pLen, pLen);
            } else {
                val = val.substr(0, pLen * 2) + "...";
            }
        }
        return val;
    }

    _hasFormatter(key) {
        if (this.formatters) {
            for (let el of Object.entries(this.formatters)) {
                if (key === el[0]) {
                    return this.formattersHandlers[el[1]];
                }
            }
        }
        return null
    }

    locationHuman(location, digits) {
        digits = digits === undefined ? 5 : digits;
        return location.lat.toFixed(digits) + ', ' + location.lng.toFixed(digits);
    }
}

/**
 * Serializes nested statistic values without throwing on circular or unsupported data.
 *
 * @param {*} value Statistic value to serialize for debug display.
 * @returns {string} JSON string when possible, otherwise a safe string fallback.
 */
function stringifyValue(value) {
    try {
        return JSON.stringify(value, null, 2);
    } catch (e) {
        return String(value);
    }
}

/**
 * Escapes special HTML characters before inserting text into generated HTML.
 *
 * @param {string} value Text that may contain HTML-sensitive characters.
 * @returns {string} Escaped text safe to place in HTML content.
 */
function escapeHtml(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}
